import { useCordovaBridge } from "./useCordovaBridge";

export type FirebaseMessagePayload = {
  fcmMessageId?: string;
  messageId?: string;
  data?: {
    type?: string;
    payload?: string;
    title?: string;
    body?: string;
    image?: string;
    click?: string;
  } | null;
  additionalData?: {
    type?: string;
    payload?: string;
  } | null;
};

type FirebaseMessagingRuntime = {
  getToken: (options?: { vapidKey?: string }) => Promise<string | null>;
  onTokenRefresh?: (listener: () => void) => void;
  onMessage: (listener: (payload: FirebaseMessagePayload) => void) => void;
};

const FIREBASE_CONFIG = {
  apiKey: "AIzaSyDox_LuL652Dh74_ZLm5WltyvHd9UnUu-0",
  authDomain: "vbote-worktime.firebaseapp.com",
  databaseURL: "https://vbote-worktime.firebaseio.com",
  projectId: "vbote-worktime",
  storageBucket: "vbote-worktime.appspot.com",
  messagingSenderId: "627744169597",
  appId: "1:627744169597:web:b68bfce9cfafb1d4",
};

const FIREBASE_VAPID_KEY =
  "BHJqaZ04fECPRfs2099mM6HAPYuUbmOZeqLx-U4tdR-xPqEKPKJGF0XigZ82UNl7rMRZkqKfVIy2Zi1xjbRE4Yc";

let firebaseMessagingInitialized = false;
let swListenerChannel: BroadcastChannel | null = null;
const recentForegroundNotificationKeys = new Map<string, number>();

function canUseWindow(): boolean {
  return typeof window !== "undefined";
}

function supportsFirebaseMessagingRuntime(): boolean {
  if (!canUseWindow() || typeof navigator === "undefined") {
    return false;
  }

  const hasNotifications = typeof Notification !== "undefined";
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasPushManager = typeof PushManager !== "undefined";
  const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const hasSecureContext = window.isSecureContext || isLocalhost;

  return hasNotifications && hasServiceWorker && hasPushManager && hasSecureContext;
}

function getFirebaseMessaging(): FirebaseMessagingRuntime | null {
  if (!supportsFirebaseMessagingRuntime()) {
    return null;
  }

  const firebase = window.firebase;
  if (!firebase?.messaging) {
    return null;
  }

  if (!Array.isArray(firebase.apps) || firebase.apps.length === 0) {
    firebase.initializeApp(FIREBASE_CONFIG);
  }

  try {
    return firebase.messaging() as unknown as FirebaseMessagingRuntime;
  } catch (error) {
    console.warn("Firebase messaging unavailable in this runtime", error);
    return null;
  }
}

function syncNotificationToken(token?: string | null): void {
  if (!token || !canUseWindow()) {
    return;
  }

  const cordovaBridge = useCordovaBridge();
  cordovaBridge.dispatchMessage(`TOKEN:${token}`);
}

function getNormalizedNotificationPayload(payload?: string): string {
  const normalizedPayload = payload?.trim().toLowerCase() || "";
  return normalizedPayload === "finish-activity" ||
    normalizedPayload === "activity-finish"
    ? "activity-finish"
    : normalizedPayload === "auto-stop-activity"
    ? "auto-stop-activity"
    : normalizedPayload;
}

function getForegroundNotificationKey(payload: FirebaseMessagePayload): string | null {
  const data = payload.data;
  if (!data?.title && !data?.body) {
    return null;
  }

  const normalizedPayload = getNormalizedNotificationPayload(data.payload);
  const normalizedType =
    normalizedPayload === "activity-finish" ? "activity-finish" : data.type ?? "";

  // Dedup basado en contenido, no en fcmMessageId, para descartar duplicados enviados
  // desde distintas rutas del servidor (ej: notifyAutoStoppedActivity + notifyEmployeeActivity)
  return [
    normalizedType,
    normalizedPayload,
    data.title ?? "",
    data.body ?? "",
  ].join("|");
}

function shouldShowForegroundNotification(payload: FirebaseMessagePayload): boolean {
  const key = getForegroundNotificationKey(payload);
  if (!key) {
    return false;
  }

  const now = Date.now();
  const lastShownAt = recentForegroundNotificationKeys.get(key);
  if (lastShownAt && now - lastShownAt < 5000) {
    return false;
  }

  recentForegroundNotificationKeys.set(key, now);
  window.setTimeout(() => {
    recentForegroundNotificationKeys.delete(key);
  }, 5000);
  return true;
}

export async function showForegroundPushNotification(
  payload: FirebaseMessagePayload,
): Promise<void> {
  if (
    !canUseWindow() ||
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  ) {
    return;
  }

  const data = payload.data;
  const title = data?.title?.trim();
  if (!title) {
    return;
  }

  if (!shouldShowForegroundNotification(payload)) {
    return;
  }

  const options: NotificationOptions = {
    body: data?.body,
    icon: data?.image,
    tag: data?.payload || data?.type || "worktime-client-push",
    data: {
      url: data?.click,
    },
  };

  // Esta función se llama exclusivamente desde onMessage (app en primer plano).
  // registration.showNotification() puede ser suprimido silenciosamente por Chrome
  // cuando la pestaña está activa — su Promise resuelve sin error aunque no muestre
  // nada visualmente. Por eso usamos new Notification() directamente para foreground,
  // que siempre muestra el popup cuando el permiso está concedido.
  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      if (data?.click) {
        window.open(data.click, "_blank", "noopener");
      }
      notification.close();
    };
    return;
  } catch {
    // Fallback to the service worker path when the foreground API is blocked.
  }

  try {
    const registration =
      "serviceWorker" in navigator ? await navigator.serviceWorker.ready : null;

    if (registration?.showNotification) {
      await registration.showNotification(title, options);
    }
  } catch {
    // Foreground notification display is best-effort.
  }
}

function emitFirebasePush(payload: FirebaseMessagePayload): void {
  void showForegroundPushNotification(payload);
  const cordovaBridge = useCordovaBridge();
  cordovaBridge.emitNativeDeviceEvent(`NE_PUSH:${JSON.stringify(payload)}`);
}

async function requestBrowserNotificationToken(
  messaging: FirebaseMessagingRuntime,
): Promise<void> {
  if (!canUseWindow() || typeof Notification === "undefined") {
    return;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return;
    }

    const token = await messaging.getToken({
      vapidKey: FIREBASE_VAPID_KEY,
    });
    syncNotificationToken(token);
  } catch (error) {
    console.warn("Unable to retrieve Firebase notification token", error);
  }
}

export function initFirebaseMessaging(): void {
  if (!canUseWindow() || firebaseMessagingInitialized) {
    return;
  }

  const messaging = getFirebaseMessaging();
  if (!messaging) {
    return;
  }

  firebaseMessagingInitialized = true;

  void requestBrowserNotificationToken(messaging);

  if (typeof messaging.onTokenRefresh === "function") {
    messaging.onTokenRefresh(() => {
      void requestBrowserNotificationToken(messaging);
    });
  }

  messaging.onMessage((payload) => {
    emitFirebasePush(payload);
  });

  if (typeof BroadcastChannel !== "undefined") {
    swListenerChannel = new BroadcastChannel("swListener");
    swListenerChannel.onmessage = (event) => {
      if (typeof event.data === "string" && event.data.startsWith("NE_PUSH:")) {
        useCordovaBridge().emitNativeDeviceEvent(event.data);
      }
    };
  }
}
